import { makeAutoObservable, computed, action, runInAction } from '../../common/stores/mobx-config.js';
import { storeEvents, EVENTS } from '../../common/stores/storeEvents.js';
import { TIMELINE_CONFIG } from './timelineConfig.js';
import { logger } from '../../utils/debug/logger.js';

const WORKER_URL = 'https://historical-events.genealogie.app/events';

/**
 * Convert a date string to a numeric sort key (YYYYMMDD).
 * Handles: DD/MM/YYYY → YYYYMMDD, MM/YYYY → YYYY0M01, YYYY-only → YYYY0101.
 * Events without a precise day/month are placed at January 1st.
 */
export function dateSortKey(evt) {
    const d = evt.date;
    if (!d) return (evt.year || 0) * 10000 + 101;
    const parts = d.split('/');
    if (parts.length === 3) {
        // DD/MM/YYYY
        return parseInt(parts[2]) * 10000 + parseInt(parts[1]) * 100 + parseInt(parts[0]);
    }
    if (parts.length === 2) {
        // MM/YYYY
        return parseInt(parts[1]) * 10000 + parseInt(parts[0]) * 100 + 1;
    }
    // Fallback: year only → position at January 1st
    return (evt.year || 0) * 10000 + 101;
}
const LOCALSTORAGE_KEY = 'geneafan_historical_events';
const LOCALSTORAGE_VERSION_KEY = 'geneafan_historical_events_version';
const LOCALSTORAGE_SCHEMA_KEY = 'geneafan_historical_events_schema';

// Bump this when the event data structure changes (e.g. category renames).
// Stale localStorage caches with an older schema are discarded on load.
const SCHEMA_VERSION = 3;

/**
 * Store for historical events loaded from Cloudflare KV via worker.
 * Provides computed views grouped by 5-year period (same key format
 * as timelineEventsStore) and filtered by scope/category.
 *
 * Loading strategy:
 * 1. Constructor loads from localStorage (instant, offline-capable)
 * 2. loadEvents() fetches from worker, updates localStorage
 * 3. Triggered by CACHE.BUILT event (in parallel with geocoding)
 *
 * Cache invalidation is server-driven: the worker response includes a
 * version field (v) that the client stores alongside the events.
 * No client-side code change is needed when the worker data is refreshed.
 */
class HistoricalEventsStore {
    events = [];
    isLoading = false;
    isReady = false;
    error = null;

    constructor() {
        makeAutoObservable(this, {
            groupedByPeriod: computed,
            warEvents: computed,
            leaderEvents: computed,
            eventIndex: computed,
            categories: computed,
            periodKeys: computed,
            loadEvents: action,
        });

        // Load from localStorage immediately (synchronous bootstrap)
        this._loadFromLocalStorage();

        // Trigger fetch when GEDCOM data is ready
        storeEvents.subscribe(EVENTS.CACHE.BUILT, () => {
            this.loadEvents();
        });
    }

    /**
     * Fetch events from the worker, with localStorage fallback.
     * Non-blocking: errors are handled internally.
     * Expects response format: { v: "<version>", events: [...] }
     */
    async loadEvents() {
        if (this.isLoading) return;

        runInAction(() => {
            this.isLoading = true;
            this.error = null;
        });

        storeEvents.emit(EVENTS.HISTORICAL_EVENTS.LOAD_START);

        try {
            // Check server version against localStorage version
            const storedVersion = parseInt(localStorage.getItem(LOCALSTORAGE_VERSION_KEY), 10) || 0;
            const metaRes = await fetch(WORKER_URL.replace('/events', '/meta'), { cache: 'no-store' });
            const meta = await metaRes.json();
            const serverVersion = parseInt(meta.version, 10) || 0;

            // If versions match and we have cached events, use them
            if (serverVersion === storedVersion && this.events.length > 0) {
                runInAction(() => { this.isReady = true; this.isLoading = false; });
                storeEvents.emit(EVENTS.HISTORICAL_EVENTS.LOAD_COMPLETE, {
                    count: this.events.length, source: 'cache'
                });
                logger.timeline.store.info(
                    `Historical events: version ${serverVersion} unchanged, using cached ${this.events.length} events`
                );
                return;
            }

            // Version changed or no cache — fetch full events
            const response = await fetch(WORKER_URL, { cache: 'no-store' });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const payload = await response.json();
            const events = Array.isArray(payload) ? payload : (payload.events || []);
            const version = payload.v || serverVersion;

            runInAction(() => {
                this.events = events;
                this.isReady = true;
                this.isLoading = false;
            });

            // Persist to localStorage for offline fallback
            this._saveToLocalStorage(events, version);

            storeEvents.emit(EVENTS.HISTORICAL_EVENTS.LOAD_COMPLETE, {
                count: events.length,
                source: 'worker'
            });

            logger.timeline.store.info(
                `Historical events loaded from worker: ${events.length} events (v=${version})`
            );

        } catch (err) {
            logger.timeline.store.warn(
                'Failed to load historical events from worker, using localStorage fallback:',
                err.message
            );

            runInAction(() => {
                this.isLoading = false;
                // If we already have localStorage data, mark as ready silently
                if (this.events.length > 0) {
                    this.isReady = true;
                    this.error = null;
                    storeEvents.emit(EVENTS.HISTORICAL_EVENTS.LOAD_COMPLETE, {
                        count: this.events.length,
                        source: 'localStorage'
                    });
                } else {
                    this.error = err.message;
                    storeEvents.emit(EVENTS.HISTORICAL_EVENTS.LOAD_ERROR, {
                        error: err.message
                    });
                }
            });
        }
    }

    // --- Computed (same API surface as before) ---

    /** Group events by 5-year period key (same format as family events: "01/01/YYYY") */
    get groupedByPeriod() {
        const yearsGroup = TIMELINE_CONFIG.YEARS_GROUP;
        const grouped = {};
        for (const event of this.events) {
            const yearStart = Math.floor(event.year / yearsGroup) * yearsGroup;
            const key = `01/01/${yearStart}`;
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(event);
        }
        for (const key of Object.keys(grouped)) {
            grouped[key].sort((a, b) => dateSortKey(a) - dateSortKey(b));
        }
        return grouped;
    }

    /** Index: id → event for O(1) lookup */
    get eventIndex() {
        const map = new Map();
        for (const e of this.events) map.set(e.id, e);
        return map;
    }

    /** Root-level parent war events for the wars band (excludes sub-conflicts) */
    get warEvents() {
        return this.events.filter(e => e.subtype === 'war' && e.isParent && !e.parentId);
    }

    /** Leader events for the leaders band */
    get leaderEvents() {
        return this.events.filter(e => e.subtype === 'leader');
    }

    /** Get events for a period, optionally filtered by scope and category.
     *  Excludes leader events (displayed in the leaders band, not as cards). */
    getEventsForPeriod(periodKey, scope = 'all', category = 'all') {
        const events = this.groupedByPeriod[periodKey] || [];
        return events.filter(e =>
            e.subtype !== 'leader' &&
            (scope === 'all' ? e.scope !== 'regions' : e.scope === scope) &&
            (category === 'all' || e.category === category)
        );
    }

    /** Get events for a specific year, optionally filtered by scope and category */
    getEventsForYear(year, scope = 'all', category = 'all') {
        return this.events.filter(e =>
            e.year === year &&
            (scope === 'all' ? e.scope !== 'regions' : e.scope === scope) &&
            (category === 'all' || e.category === category)
        );
    }

    /** All distinct categories present in the dataset */
    get categories() {
        return [...new Set(this.events.map(e => e.category))].sort();
    }

    /** All period keys that contain historical events */
    get periodKeys() {
        return Object.keys(this.groupedByPeriod);
    }

    // --- Private helpers ---

    /**
     * Load events from localStorage for instant display.
     * Always loads if data exists — the worker fetch will refresh it.
     */
    _loadFromLocalStorage() {
        try {
            // Discard stale caches from before a schema-breaking change
            const storedSchema = parseInt(localStorage.getItem(LOCALSTORAGE_SCHEMA_KEY), 10) || 0;
            if (storedSchema < SCHEMA_VERSION) {
                localStorage.removeItem(LOCALSTORAGE_KEY);
                localStorage.removeItem(LOCALSTORAGE_VERSION_KEY);
                localStorage.removeItem(LOCALSTORAGE_SCHEMA_KEY);
                logger.timeline.store.info(
                    `Historical events cache invalidated (schema ${storedSchema} < ${SCHEMA_VERSION})`
                );
                return;
            }

            const cached = localStorage.getItem(LOCALSTORAGE_KEY);
            if (cached) {
                const parsed = JSON.parse(cached);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    this.events = parsed;
                    this.isReady = true;
                    const version = localStorage.getItem(LOCALSTORAGE_VERSION_KEY) || '?';
                    logger.timeline.store.debug(
                        `Historical events loaded from localStorage: ${parsed.length} events (v=${version})`
                    );
                }
            }
        } catch (e) {
            logger.timeline.store.warn('Failed to load historical events from localStorage:', e);
        }
    }

    /**
     * Save events + server version to localStorage.
     * @param {Array} events - Events array
     * @param {string|null} version - Server version from worker response
     */
    _saveToLocalStorage(events, version) {
        try {
            localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(events));
            localStorage.setItem(LOCALSTORAGE_SCHEMA_KEY, String(SCHEMA_VERSION));
            if (version) {
                localStorage.setItem(LOCALSTORAGE_VERSION_KEY, version);
            }
        } catch (e) {
            logger.timeline.store.warn('Failed to save historical events to localStorage:', e);
        }
    }
}

const historicalEventsStore = new HistoricalEventsStore();
export default historicalEventsStore;
